import { useCallback, useEffect, useRef, useState } from "react";
import { CheckCircle2, QrCode, RefreshCcw, X } from "lucide-react";

type LoginStatus = {
  state: "online" | "offline" | "waiting_scan" | "qr_expired" | "restarting" | "unavailable";
  online: boolean;
  uin: string | null;
  nickname: string | null;
  qr_available: boolean;
  message: string;
  checked_at: string;
};
type LoginQR = LoginStatus & { qr_data_url: string | null };

async function loginFetch<T>(path: string, body?: object): Promise<T> {
  const response = await fetch(`/api/qq-login/${path}`, {
    credentials: "same-origin", cache: "no-store",
    ...(body ? { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) } : {}),
  });
  if (!response.ok) {
    if (response.status === 401) throw new Error("后台登录已失效，请刷新页面重新登录。");
    const data = await response.json().catch(() => ({}));
    throw new Error(typeof data.detail === "string" ? data.detail : "QQ 登录状态暂时不可用，请重试。");
  }
  return response.json();
}

const labels: Record<LoginStatus["state"], string> = {
  online: "QQ 已在线", offline: "QQ 已掉线", waiting_scan: "QQ 待扫码登录",
  qr_expired: "登录二维码已过期", restarting: "QQ 正在重新连接", unavailable: "QQ 状态暂不可用",
};

export function QQLoginPanel() {
  const [status, setStatus] = useState<LoginStatus | null>(null);
  const [open, setOpen] = useState(false);
  const [qr, setQR] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState("");
  const [autoRequested, setAutoRequested] = useState(false);
  const dialog = useRef<HTMLDivElement>(null);
  const active = useRef(true);
  const pendingStatus = useRef(false);
  const pendingQR = useRef(false);

  const loadStatus = useCallback(async () => {
    if (pendingStatus.current || pendingQR.current) return;
    pendingStatus.current = true;
    setChecking(true);
    try {
      const next = await loginFetch<LoginStatus>("status");
      if (!active.current || pendingQR.current) return;
      setStatus(next);
      setError("");
      if (next.online) setQR(null);
      // A refresh can finish before NapCat publishes a different URL. Retry
      // reading the available QR on the next status poll, without refreshing it.
      if (next.qr_available && !next.online) setAutoRequested(false);
    } catch (e) {
      if (active.current) {
        setError(e instanceof Error ? e.message : "读取 QQ 状态失败。");
        setStatus((previous) => previous ? { ...previous, state: "unavailable", online: false } : null);
      }
    } finally {
      pendingStatus.current = false;
      if (active.current) setChecking(false);
    }
  }, []);

  useEffect(() => {
    active.current = true;
    if (!open) void loadStatus();
    const interval = window.setInterval(() => {
      if (!document.hidden) void loadStatus();
    }, open ? 3000 : 15000);
    return () => { active.current = false; window.clearInterval(interval); };
  }, [open, loadStatus]);

  const requestQR = useCallback(async (refresh: boolean) => {
    if (pendingQR.current) return;
    pendingQR.current = true;
    setBusy(true);
    setError("");
    setQR(null);
    setAutoRequested(true);
    try {
      const next = await loginFetch<LoginQR>("qrcode", { refresh });
      if (!active.current) return;
      setStatus(next);
      setQR(next.qr_data_url);
      if (next.state === "restarting") setAutoRequested(false);
    } catch (e) {
      if (active.current) setError(e instanceof Error ? e.message : "获取登录二维码失败。");
    } finally {
      pendingQR.current = false;
      if (active.current) setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (open && !qr && !busy && !autoRequested && status?.qr_available && !status.online) {
      void requestQR(false);
    }
  }, [open, qr, busy, autoRequested, status, requestQR]);

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialog.current?.querySelector<HTMLButtonElement>("button")?.focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
      if (event.key !== "Tab") return;
      const buttons = dialog.current?.querySelectorAll<HTMLButtonElement>("button:not(:disabled)");
      if (!buttons?.length) return;
      const first = buttons[0], last = buttons[buttons.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", keydown);
    return () => { document.removeEventListener("keydown", keydown); previous?.focus(); };
  }, [open]);

  const name = [status?.nickname, status?.uin].filter(Boolean).join(" · ");
  const showQR = qr && status?.state === "waiting_scan";
  return <>
    <section className="qq-login-bar" aria-label="QQ 机器人登录状态">
      <div className="qq-login-summary" aria-live="polite">
        <span className={`qq-login-dot ${status?.online ? "is-online" : ""}`} />
        <div><strong>{status ? labels[status.state] : "正在读取 QQ 登录状态…"}</strong>
          <p>{name || "QQ 机器人"}{status?.online ? " · 通知和命令可用" : " · 可在此扫码重新登录"}</p>
        </div>
      </div>
      <div className="qq-login-actions">
        <button type="button" className="btn-outline" onClick={() => void loadStatus()} disabled={checking}>
          <RefreshCcw size={15} className={checking ? "spin" : ""} />刷新状态
        </button>
        {!status?.online && <button type="button" className="btn-primary" disabled={busy} onClick={() => {
          setOpen(true); void requestQR(status?.state === "qr_expired");
        }}><QrCode size={16} />扫码重新登录</button>}
      </div>
      {error && !open && <p className="qq-login-error" role="alert">{error}</p>}
    </section>
    {open && <div className="qq-login-overlay" onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
      <div className="qq-login-dialog" role="dialog" aria-modal="true" aria-labelledby="qq-login-title" ref={dialog}>
        <div className="qq-login-dialog-head"><h2 id="qq-login-title">QQ 机器人登录</h2>
          <button type="button" className="btn-ghost" aria-label="关闭 QQ 登录窗口" onClick={() => setOpen(false)}><X size={20} /></button>
        </div>
        <p className="qq-login-account">{name || "请使用机器人账号的手机 QQ 扫码"}</p>
        <div className="qq-login-qr">
          {status?.online ? <div className="qq-login-success"><CheckCircle2 size={44} /><strong>QQ 已登录成功</strong></div>
            : showQR ? <img src={qr!} alt="QQ 机器人登录二维码" width="252" height="252" />
            : <div className="qq-login-placeholder"><QrCode size={42} />
              <span>{busy ? "正在生成登录二维码…" : status?.state === "qr_expired" ? "二维码已过期" : status?.state === "restarting" ? "正在重新连接 QQ…" : "等待登录二维码"}</span></div>}
        </div>
        <p className="qq-login-hint" aria-live="polite">{status?.message || "扫码后请在手机 QQ 上确认登录。"}</p>
        {error && <p className="qq-login-error" role="alert">{error}</p>}
        <div className="qq-login-dialog-actions">
          {status?.online ? <button type="button" className="btn-primary" onClick={() => setOpen(false)}>完成</button>
            : <button type="button" className="btn-outline" disabled={busy || status?.state === "restarting"} onClick={() => void requestQR(true)}>
              <RefreshCcw size={15} className={busy ? "spin" : ""} />刷新二维码</button>}
          <button type="button" className="btn-outline" onClick={() => setOpen(false)}>关闭</button>
        </div>
      </div>
    </div>}
  </>;
}
